import { apiFetchWithRetry } from '@/lib/apiFetch';

export interface SubjectView {
  subjectId: string;
  subjectName: string;
  topicIds: string[];
  topicNames: string[];
}

export interface SubjectExpertiseView {
  subjectId: string;
  subjectName: string;
  depthLevel: string;
}

export interface ExamSuccessStoryView {
  studentCount: number;
  achievement: string;
  threshold?: string | null;
  subjectExamId?: string | null;
  examName?: string | null;
  timeframe: string;
}

export interface IntroVideoView {
  videoId?: string | null;
  status?: string | null;
  playbackUrl?: string | null;
  thumbnailUrl?: string | null;
  oembedHtml?: string | null;
}

export interface PublicTeacherProfile {
  handle: string;
  fullName: string;
  phone?: string | null;
  teachingModes: string[];
  subjects: SubjectView[];
  professionalBio?: string | null;
  teachingApproach?: string | null;
  profileImage?: string | null;
  introVideo?: IntroVideoView | null;
  placeId?: string | null;
  locationDisplayName?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  teachingAudience: string[];
  curriculumSystems: string[];
  instructionLanguages: string[];
  otherInstructionLanguage?: string | null;
  subjectExpertise: SubjectExpertiseView[];
  examSuccessStories: ExamSuccessStoryView[];
  highestQualification?: string | null;
  highestQualificationInstitution?: string | null;
  teachingQualification?: string | null;
  yearsTeachingExperience?: string | null;
  formerSchoolTeacher?: boolean | null;
  formerInstitutionName?: string | null;
  formerInstitutionYears?: number | null;
  overallStatus?: string | null;
  teacherUserId?: string | null;
  /** Active subscription tier; null when the teacher has no active subscription */
  teacherTier?: 'SILVER' | 'GOLD' | 'PLATINUM' | null;
}

export interface PricingPlanView {
  id?: string;
  currency: string;
  regularPrice: number;
  earlyBirdPrice?: number | null;
  trialSessionPrice?: number | null;
  perSessionEquivalent?: number | null;
  earlyBirdPerSessionEquivalent?: number | null;
}

export interface AvailabilitySlot {
  dayOfWeek: string;
  timeFrom: string;
  timeTo: string;
}

export interface ComputedFields {
  seatsRemaining?: number | null;
  currentEnrollment?: number | null;
  batchPricePerSession?: number | null;
}

export interface PublicOffering {
  id: string;
  teacherHandle: string;
  slug?: string;
  offeringType: 'ONE_TO_ONE' | 'GROUP' | 'CRASH_COURSE';
  title: string;
  description?: string | null;
  subjectId: string;
  subjectName: string;
  gradeBands: string[];
  curriculumSystems: string[];
  topicIds: string[];
  topicNames: string[];
  pricingPlans: PricingPlanView[];
  trialSessionAvailable: boolean;
  trialSessionPrice?: number | null;
  teachingMode: string;
  availabilitySlots?: AvailabilitySlot[] | null;
  computedFields?: ComputedFields | null;
  oneToOne?: { sessionDurationMinutes?: number | null } | null;
  group?: {
    batchSizeMin?: number | null;
    batchSizeMax?: number | null;
    nextBatchStartDate?: string | null;
    rollingEnrollment?: boolean | null;
  } | null;
  crashCourse?: {
    courseStartDate?: string | null;
    courseEndDate?: string | null;
    seatsTotal?: number | null;
  } | null;
}

export const getTeacherByHandle = async (handle: string): Promise<PublicTeacherProfile> => {
  const response = await apiFetchWithRetry(`/api/v1/marketplace/teachers/${handle}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch teacher profile: ${response.status}`);
  }
  return response.json();
};

export const getPublicOfferingByHandleAndSlug = async (
  handle: string,
  slug: string,
): Promise<PublicOffering | null> => {
  const response = await apiFetchWithRetry(
    `/api/v1/marketplace/teachers/${handle}/offerings/${slug}`,
    { method: 'GET', headers: { 'Content-Type': 'application/json' } },
  );
  if (!response.ok) return null;
  return response.json();
};

export const getTeacherOfferings = async (handle: string): Promise<PublicOffering[]> => {
  const response = await apiFetchWithRetry(`/api/v1/marketplace/teachers/${handle}/offerings`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch teacher offerings: ${response.status}`);
  }
  const data = await response.json();
  return data.offerings ?? [];
};
